// -----------------------------------------------------------------------------
// AIDAS Platform — useData.js
// -----------------------------------------------------------------------------
//  Safe data fetch hook that automatically injects auth token
//  Waits for token from AuthContext before requesting
//  Prevents 401s and "token is not defined" errors
//  Supports Silent Refresh (background updates)
// -----------------------------------------------------------------------------

import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch } from "../api";
import { useAuth } from "../context/useAuth";

/**
 * useData - Generic hook to fetch protected data from API endpoints.
 * Automatically includes authentication token and re-fetches when token changes.
 *
 * @param {string} endpoint - The API endpoint (e.g. "/users/me")
 * @returns {{ data: any, isLoading: boolean, error: Error | null, refresh: (silent?: boolean) => void, setData: Function }}
 */
export const useData = (endpoint) => {
  const { token, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchedEndpoint, setLastFetchedEndpoint] = useState(null);
  // Used to force a re-fetch on demand (refresh() increments this)
  const [refreshTick, setRefreshTick] = useState(0);

  const fetchData = useCallback(async (silent = false) => {
    if (!endpoint) {
      setIsLoading(false);
      return;
    }
    try {
      // Background update? only if we have data and it's THE SAME endpoint
      const isActuallySilent = silent && !!data && (endpoint === lastFetchedEndpoint);
      if (!isActuallySilent) setIsLoading(true);

      const result = await apiFetch(endpoint, token);

      // REFERENTIAL STABILITY: Only update if data actually changed
      if (JSON.stringify(data) !== JSON.stringify(result)) {
        setData(result);
        setLastFetchedEndpoint(endpoint);
      }

      setError(null);
    } catch (err) {
      // 410 Gone = feature retired. Treat as empty list so consumers don't hang.
      if (err?.status === 410 || err?.message?.includes('410')) {
        setData([]);
        setError(null);
      } else {
        console.error(`[useData] Failed to fetch ${endpoint}:`, err);
        setError(err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [endpoint, token, data, lastFetchedEndpoint]);

  // Keep a ref to the latest fetchData so refresh() is never stale
  const fetchDataRef = useRef(fetchData);
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    if (authLoading || !token) return;

    // If it's the exact same endpoint we already have, do a background update (silent)
    const isBackgroundRefresh = !!data && endpoint === lastFetchedEndpoint && refreshTick === 0;
    fetchData(isBackgroundRefresh);
  }, [authLoading, token, endpoint, refreshTick]); // refreshTick forces a re-fetch on refresh()

  return {
    data,
    isLoading: isLoading || authLoading || !token,
    error,
    // refresh() always calls the LATEST fetchData via ref — no stale closure
    refresh: (silent = false) => {
      if (silent) {
        fetchDataRef.current(true);
      } else {
        setRefreshTick(t => t + 1);
      }
    },
    setData,
  };
};

export default useData;